// src/app/admin/admin-forms/services/form-state.service.ts

import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, map, distinctUntilChanged } from 'rxjs';
import { FormSchema } from '../models/form-schema.model';
import { FormStateSnapshot } from '../models/form-output.model';
import { ConditionEngineService } from './condition-engine.service';

@Injectable({ providedIn: 'root' })
export class FormStateService {

  private state$ = new BehaviorSubject<FormStateSnapshot | null>(null);

  constructor(private conditionEngine: ConditionEngineService) {}

  track(schema: FormSchema, form: FormGroup): void {
    // emitir estado inicial
    this.emit(schema, form);

    // emitir en cada cambio de valor o estado
    form.valueChanges.subscribe(() => this.emit(schema, form));
    form.statusChanges.subscribe(() => this.emit(schema, form));
  }

  // snapshot completo
  getSnapshot(): Observable<FormStateSnapshot | null> {
    return this.state$.asObservable();
  }

  // selectores individuales con distinctUntilChanged
  isValid$(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s?.valid ?? false),
      distinctUntilChanged()
    );
  }

  isDirty$(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s?.dirty ?? false),
      distinctUntilChanged()
    );
  }

  isPending$(): Observable<boolean> {
    return this.state$.pipe(
      map(s => s?.pending ?? false),
      distinctUntilChanged()
    );
  }

  getValue$(): Observable<Record<string, unknown>> {
    return this.state$.pipe(
      map(s => s?.value ?? {}),
      distinctUntilChanged(
        (a, b) => JSON.stringify(a) === JSON.stringify(b)
      )
    );
  }

  private emit(schema: FormSchema, form: FormGroup): void {
    const allFields = schema.sections.flatMap(s => s.fields);

    const hiddenFields = allFields
      .filter(f => this.conditionEngine.getState(f.id).hidden)
      .map(f => f.id);

    const disabledFields = allFields
      .filter(f => form.get(f.id)?.disabled)
      .map(f => f.id);

    this.state$.next({
      formId:        schema.id,
      value:         form.getRawValue(),
      valid:         form.valid,
      invalid:       form.invalid,
      dirty:         form.dirty,
      pristine:      form.pristine,
      touched:       form.touched,
      pending:       form.pending,
      hiddenFields,
      disabledFields,
    });
  }
}